/**
 * Base functions for deciders
 * These are functions that plugins can call
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var bucketing = require('./bucketing');
var condition = require('../lib/condition');
var DecisionError = require('./error').DecisionError;
var Flux = require('../flux');
var Logger = require('../utils/logger');
var cmabHelper = require('plugins/feature_cmab/cmabHelper');


/**
 * @constant
 * @type {string}
 * @private
 */
var IMPRESSION = 'impression';

/**
 * Evaluate whether the visitor's audienceIds satisfy the experiment's audienceId conditions
 * @param {string[]} audienceIds ids of audiences that the visitor satisfies
 * @param {Experiment} experiment
 * @returns {boolean}
 */
exports.isValidExperiment = function(audienceIds:string[], experiment:Experiment): boolean {
  var evaluator = _.partial(_.includes, audienceIds);

  var result;
  Logger.groupCollapsed('Decision / Evaluating audiences for experiment:', experiment, audienceIds);
  if (!experiment.audienceIds) {
    result = true;
  } else {
    result = condition.evaluate(experiment.audienceIds, evaluator);
  }
  Logger.groupEnd();
  Logger.debug('Decision / Experiment', experiment, 'is valid?', result);
  return result;
};

/**
 * Select variation the visitor should be bucketed into given an experiment, bucketingId, and
 * a preferredVariationMap
 * @param {Experiment} experiment
 * @param {string[]} audienceIds Optionally, audienceIds can be used to decide
 * @param {string} bucketingId
 * @param {string} activationId
 * @param {Object} [preferredVariationMap]
 * @returns {Variation}
 */
exports.selectVariation = function(experiment:Experiment, audienceIds:string[], bucketingId:string, activationId:string, preferredVariationMap?:any): Variation {
  Logger.debug('Decision / Selecting variation for experiment:', experiment.id);

  if (!experiment.variations || experiment.variations.length === 0) {
    throw new DecisionError('No variations in selected experiment "' + experiment.id + '"');
  }
  if (!experiment.weightDistributions && experiment.variations.length > 1) {
    throw new DecisionError('On selected experiment "' + experiment.id +
                    '", weightDistributions must be defined if # variations > 1');
  }

  var selectedVariationId;

  if (experiment.bucketingStrategy && experiment.bucketingStrategy === IMPRESSION) {
    if (experiment.variations.length === 1) {
      selectedVariationId = experiment.variations[0].id;
    } else {
      // To enable non-deterministic bucketing, use activationId to get a seed that's unique per client activation
      var randomSeed = activationId;
      selectedVariationId = bucketing.chooseWeightedCandidate(bucketingId + randomSeed, experiment.id, experiment.weightDistributions);
    }
  } else {
    if (experiment.variations.length === 1) {
      selectedVariationId = experiment.variations[0].id;
    } else {
      selectedVariationId = bucketing.chooseWeightedCandidate(bucketingId, experiment.id, experiment.weightDistributions);
    }

    if (experiment?.deployed) {
      Logger.debug('Decision / Experiment is deployed. Skipping bucketing.');
    }

    else if (preferredVariationMap && preferredVariationMap[experiment.id]) {
      Logger.debug('Decision / Using preferredVariationMap to select variation for experiment:', experiment.id);
      var preferredVariationId = preferredVariationMap[experiment.id];
      if (!_.find(experiment.variations, {id: preferredVariationId})) {
        // Preferred variation no longer exists - drop from layer
        Flux.dispatch(actionTypes.RECORD_STICKY_BUCKETING_FEATURE, {
          feature: 'stoppedVariation',
          id: experiment.id,
        });
        Logger.debug('Decision / Preferred variation:', preferredVariationId,
                    'not found on experiment:', experiment.id, '. Visitor not bucketed.');
        return null;
      }

      if (preferredVariationId !== selectedVariationId) {
        // Record when the preferred variation is NOT the one that we would get "naturally"
        Flux.dispatch(actionTypes.RECORD_STICKY_BUCKETING_FEATURE, {
          feature: 'preferredVariation',
          id: experiment.id,
        });
        selectedVariationId = preferredVariationId;
      }
    }
  }

  // CMAB Specific code
  var cmabVariationId = cmabHelper.processCmabExperiment(experiment, selectedVariationId);
  if (cmabVariationId) {
    selectedVariationId = cmabVariationId;
  }
  // CMAB Specific code ends

  var selectedVariation = _.find(experiment.variations, {id: selectedVariationId});

  if (selectedVariation) {
    Logger.debug('Decision / Selected variation:', selectedVariation);
    return selectedVariation;
  }
  throw new DecisionError('Unable to find selected variation: "' + selectedVariationId + '".');
};

/**
 * Get experiment given its id
 * @param {Layer} layer
 * @param {string} experimentId
 * @returns {Experiment}
 */
exports.getExperimentById = function(layer:Layer, experimentId:string): Experiment {
  var selectedExperiment = _.find(layer.experiments, {id: experimentId});
  if (selectedExperiment) {
    return selectedExperiment;
  }
  throw new DecisionError('Unable to find selected experiment.');
};

/**
 * Helper function to determine whether the selected variation has actions on the current view
 * @param {Variation} variation
 * @param {string} currentPageId
 * @returns {boolean}
 */
exports.hasVariationActionsOnView = function(variation:Variation, currentPageId:string): boolean {
  Logger.debug('Decision / Checking variation:', variation, 'for actions on pageId:', currentPageId);
  return !!_.find(variation.actions, function(action) {
    return action.pageId === currentPageId && !_.isEmpty(action.changes);
  });
};
